import { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useSidebar } from "@/hooks/useSidebar";
import NotificationBell from "@/components/feature/NotificationBell";

const REF_SESSION_KEY = "mad_ref_code";

const getStoredRefCode = (): string => {
  try {
    return sessionStorage.getItem(REF_SESSION_KEY) || "";
  } catch {
    return "";
  }
};

const setStoredRefCode = (code: string) => {
  try {
    sessionStorage.setItem(REF_SESSION_KEY, code);
  } catch {
    // no-op
  }
};

// Read ref code from URL + sessionStorage synchronously so the
// stripped-down navbar renders on the very first frame — no flash.
const getInitialRefCode = (): string => {
  try {
    const params = new URLSearchParams(window.location.search);
    const urlRef = params.get("ref");
    if (urlRef) {
      setStoredRefCode(urlRef);
      return urlRef;
    }
  } catch {
    // no-op — fall through to sessionStorage
  }
  return getStoredRefCode();
};

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [avatarError, setAvatarError] = useState(false);
  const [balancePulse, setBalancePulse] = useState(false);
  const prevBalanceRef = useRef<number | undefined>(undefined);
  const location = useLocation();
  const { isLoggedIn, user, logout } = useAuth();
  const { toggle: toggleSidebar } = useSidebar();

  const name = user?.name || "U";
  const hasAvatar = !!user?.avatarUrl;
  const isReseller = user?.role === "reseller";
  const resellerId = user?.resellerId || null;
  const gradients = [
    "from-primary-400 to-primary-600",
    "from-primary-300 to-foreground-700",
    "from-foreground-700 to-primary-500",
    "from-primary-500 to-foreground-900",
    "from-primary-200 to-primary-600",
    "from-foreground-800 to-primary-400",
  ];
  const gradientIndex = name.charCodeAt(0) % gradients.length;
  const gradient = gradients[gradientIndex];

  // ── Reseller referral code detection (sessionStorage-backed) ──
  const [refCode, setRefCode] = useState(getInitialRefCode);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const urlRef = params.get("ref");
    if (urlRef) {
      setStoredRefCode(urlRef);
      setRefCode(urlRef);
    } else {
      // Keep whatever's already in sessionStorage
      const stored = getStoredRefCode();
      if (stored !== refCode) setRefCode(stored);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  // ── When not logged in AND visiting from a reseller link, strip navbar ──
  const isGuestResellerVisit = !isLoggedIn && Boolean(refCode);

  useEffect(() => {
    setAvatarError(false);
  }, [user?.avatarUrl]);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Trigger pulse animation when balance increases (new commission)
  useEffect(() => {
    const currentBalance = user?.cashbackBalance;
    const prevBalance = prevBalanceRef.current;

    if (
      prevBalance !== undefined &&
      currentBalance !== undefined &&
      currentBalance > prevBalance &&
      currentBalance > 0
    ) {
      setBalancePulse(true);
      const timer = setTimeout(() => setBalancePulse(false), 3500);
      prevBalanceRef.current = currentBalance;
      return () => clearTimeout(timer);
    }

    if (currentBalance !== undefined && prevBalanceRef.current === undefined) {
      prevBalanceRef.current = currentBalance;
    } else if (currentBalance !== undefined) {
      prevBalanceRef.current = currentBalance;
    }
  }, [user?.cashbackBalance]);

  // Close profile dropdown on outside click
  useEffect(() => {
    if (!profileOpen) return;
    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest(".profile-menu")) setProfileOpen(false);
    };
    document.addEventListener("click", handleClick);
    return () => document.removeEventListener("click", handleClick);
  }, [profileOpen]);

  const showLight = scrolled || location.pathname !== "/";
  const showBareLogo = isGuestResellerVisit && !isLoggedIn;

  // ── Bare logo-only navbar for guest reseller-link visitors ──
  if (showBareLogo) {
    return (
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled ? "bg-white/95 backdrop-blur-md border-b border-accent-100" : "bg-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 md:px-6 flex items-center justify-center h-16">
          {isLoggedIn ? (
            <Link to="/" className="flex items-center gap-2">
              <img
                src="https://static.readdy.ai/image/4371883fa286ac0088227d507facd13a/cee86cc6ab83fcd7b536d5a6d3288f01.png"
                alt="Man Aya DataLink Logo"
                loading="eager"
                fetchpriority="high"
                className="h-8 sm:h-10 w-auto object-contain"
              />
            </Link>
          ) : (
            <span className="flex items-center gap-2">
              <img
                src="https://static.readdy.ai/image/4371883fa286ac0088227d507facd13a/cee86cc6ab83fcd7b536d5a6d3288f01.png"
                alt="Man Aya DataLink Logo"
                loading="eager"
                fetchpriority="high"
                className="h-8 sm:h-10 w-auto object-contain"
              />
            </span>
          )}
        </div>
      </nav>
    );
  }

  // ── Full navbar for everyone else ──
  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-white/95 backdrop-blur-md border-b border-accent-100" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-6 flex items-center justify-between h-16">
        {/* Logo + Sidebar Toggle */}
        <div className="flex items-center gap-3">
          <button
            onClick={toggleSidebar}
            className={`w-9 h-9 flex items-center justify-center rounded-lg cursor-pointer transition-colors ${
              showLight
                ? "text-foreground-700 hover:bg-foreground-100"
                : "text-white/90 hover:bg-white/10"
            }`}
            aria-label="Toggle sidebar menu"
          >
            <i className="ri-menu-line text-xl"></i>
          </button>
          <Link to="/" className="flex items-center gap-2">
            <img
              src="https://static.readdy.ai/image/4371883fa286ac0088227d507facd13a/cee86cc6ab83fcd7b536d5a6d3288f01.png"
              alt="Man Aya DataLink Logo"
              loading="eager"
              fetchpriority="high"
              className="h-10 w-auto object-contain"
            />
          </Link>
        </div>

        {/* Auth / Profile Section */}
        <div className="flex items-center gap-3">
          {isLoggedIn ? (
            <div className="relative profile-menu flex items-center gap-1 sm:gap-2">
              {isReseller && resellerId && (
                <NotificationBell resellerId={resellerId} scrolled={showLight} />
              )}
              {isReseller && (
                <>
                  <style>{`
                    @keyframes balanceGlow {
                      0%, 100% { box-shadow: 0 0 0 0 rgba(61, 27, 92, 0.35), 0 0 0 0 rgba(61, 27, 92, 0.2); transform: scale(1); }
                      25% { box-shadow: 0 0 14px 4px rgba(61, 27, 92, 0.55), 0 0 24px 6px rgba(61, 27, 92, 0.25); transform: scale(1.04); }
                      50% { box-shadow: 0 0 8px 2px rgba(61, 27, 92, 0.35), 0 0 16px 3px rgba(61, 27, 92, 0.15); transform: scale(1); }
                      75% { box-shadow: 0 0 14px 4px rgba(61, 27, 92, 0.55), 0 0 24px 6px rgba(61, 27, 92, 0.25); transform: scale(1.04); }
                    }
                    .balance-pulse { animation: balanceGlow 1.6s ease-in-out 2; }
                  `}</style>
                  <Link
                    to="/wallet"
                    className={`flex items-center gap-1.5 text-xs font-semibold whitespace-nowrap cursor-pointer rounded-full px-2 sm:px-2.5 py-1 sm:py-1.5 transition-colors ${
                      balancePulse ? "balance-pulse" : ""
                    } ${
                      showLight
                        ? "bg-accent-50 text-accent-700 hover:bg-accent-100 border border-accent-200"
                        : "bg-white/15 text-white hover:bg-white/25 border border-white/20"
                    }`}
                  >
                    <i className="ri-wallet-3-line text-sm"></i>
                    <span>
                      {user?.cashbackBalance != null
                        ? `GH₵${user.cashbackBalance.toFixed(2)}`
                        : "GH₵0.00"}
                    </span>
                  </Link>
                </>
              )}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setProfileOpen(!profileOpen);
                }}
                className={`flex items-center gap-2 text-sm font-medium whitespace-nowrap cursor-pointer transition-colors px-3 py-1.5 rounded-full ${
                  showLight
                    ? "text-foreground-700 hover:bg-foreground-100"
                    : "text-white/90 hover:bg-white/10"
                }`}
              >
                {hasAvatar && !avatarError ? (
                  <span className="w-8 h-8 rounded-full overflow-hidden flex-shrink-0">
                    <img
                      src={user?.avatarUrl || ""}
                      alt={name}
                      className="w-full h-full object-cover"
                      referrerPolicy="no-referrer"
                      onError={() => setAvatarError(true)}
                    />
                  </span>
                ) : (
                  <span className={`w-8 h-8 flex items-center justify-center rounded-full bg-gradient-to-br ${gradient} text-white text-xs font-bold flex-shrink-0`}>
                    {name.charAt(0).toUpperCase()}
                  </span>
                )}
                <span className="max-w-[100px] truncate hidden sm:inline">{name}</span>
                <i className={`ri-arrow-down-s-line text-xs transition-transform hidden sm:inline ${profileOpen ? "rotate-180" : ""}`}></i>
              </button>
              {profileOpen && (
                <div className="absolute right-0 top-full w-48 bg-white border border-foreground-100 rounded-xl shadow-lg py-1 z-50">
                  <Link
                    to="/profile"
                    onClick={() => setProfileOpen(false)}
                    className="flex items-center gap-2 px-4 py-2.5 text-sm text-foreground-700 hover:bg-foreground-50 cursor-pointer"
                  >
                    <i className="ri-user-line text-foreground-400"></i>
                    My Account
                  </Link>
                  {user?.role === "reseller" && (
                    <Link
                      to="/dashboard"
                      onClick={() => setProfileOpen(false)}
                      className="flex items-center gap-2 px-4 py-2.5 text-sm text-foreground-700 hover:bg-foreground-50 cursor-pointer"
                    >
                      <i className="ri-dashboard-line text-foreground-400"></i>
                      Reseller Dashboard
                    </Link>
                  )}
                  {user?.role === "reseller" && (
                    <Link
                      to="/wallet"
                      onClick={() => setProfileOpen(false)}
                      className="flex items-center gap-2 px-4 py-2.5 text-sm text-foreground-700 hover:bg-foreground-50 cursor-pointer"
                    >
                      <i className="ri-wallet-3-line text-foreground-400"></i>
                      Wallet
                      {user?.cashbackBalance != null && (
                        <span className="ml-auto text-xs font-bold text-primary-600 bg-primary-50 px-1.5 py-0.5 rounded">
                          GH₵{user.cashbackBalance.toFixed(2)}
                        </span>
                      )}
                    </Link>
                  )}
                  {user?.role === "admin" && (
                    <Link
                      to="/admin"
                      onClick={() => setProfileOpen(false)}
                      className="flex items-center gap-2 px-4 py-2.5 text-sm text-foreground-700 hover:bg-foreground-50 cursor-pointer"
                    >
                      <i className="ri-shield-star-line text-foreground-400"></i>
                      Admin Panel
                    </Link>
                  )}
                  <div className="border-t border-foreground-100 my-1"></div>
                  <button
                    onClick={() => { logout(); setProfileOpen(false); }}
                    className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 cursor-pointer"
                  >
                    <i className="ri-logout-box-r-line"></i>
                    Sign Out
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="flex items-center gap-3">
              <Link
                to="/login"
                className={`hidden sm:inline text-sm font-medium whitespace-nowrap cursor-pointer transition-colors ${
                  showLight ? "text-foreground-700 hover:text-accent-600" : "text-white/90 hover:text-white"
                }`}
              >
                Sign In
              </Link>
              <Link
                to="/register"
                className="bg-accent-500 hover:bg-accent-600 text-white text-sm font-semibold px-5 py-2 rounded-full whitespace-nowrap cursor-pointer transition-colors"
              >
                Sign Up
              </Link>
              <Link
                to="/reseller"
                className={`hidden sm:inline-flex text-sm font-medium px-4 py-2 rounded-full whitespace-nowrap cursor-pointer transition-colors border ${
                  showLight
                    ? "border-primary-300 text-primary-700 hover:bg-primary-50"
                    : "border-white/40 text-white hover:bg-white/10"
                }`}
              >
                Become a Reseller
              </Link>
            </div>
          )}
        </div>
      </div>
    </nav>
  );
};

export default Navbar;